import { useMemo } from 'react'
import { Box, BoxProps, Container, Grid, Typography } from '@mui/material'
import parse from 'html-react-parser'
import Image from 'next/image'

import { isHappa, useEmblaCarouselHook } from '@/hooks'

import { Button } from '../base'
import EmblaControls from '../base/embla-controls'
import { WPImage, WPLink } from '@/types'

export type BannerSliderProps = Omit<BoxProps, 'content'> &
  Partial<{
    is_first: boolean
    content: {
      variant: 'text-left' | 'text-right'
      title: string
      text: string
      link: WPLink
      image: WPImage
    }[]
  }>

const BannerSlider = ({ is_first, content = [], ...props }: BannerSliderProps) => {
  const {
    emblaRef,
    emblaApi,
    scrollSnaps,
    selectedIndex,
    nextBtnDisabled,
    prevBtnDisabled,
    scrollNext,
    scrollPrev
  } = useEmblaCarouselHook({
    align: 'start',
    skipSnaps: false,
    active: content.length > 1
  })

  const needsControls = useMemo(
    () => !(nextBtnDisabled && prevBtnDisabled),
    [nextBtnDisabled, prevBtnDisabled]
  )

  return (
    <Box {...props}>
      <Container>
        <Box
          ref={emblaRef}
          sx={{
            overflow: 'hidden',
            borderRadius: (theme) =>
              isHappa ? theme.shape.borderRadius2 : theme.shape.borderRadius7
          }}
        >
          <Box
            sx={{
              display: 'flex',
              marginLeft: { xs: -2, sm: -3, md: -4 },
              touchAction: 'pan-y pinch-zoom'
            }}
          >
            {content.map((item, index) => (
              <Grid
                container
                key={`item-${index}`}
                sx={{
                  minWidth: 0,
                  flexShrink: 0,
                  flexGrow: 0,
                  flexBasis: '100%',
                  paddingLeft: { xs: 2, sm: 3, md: 4 },
                  display: { xs: 'grid', sm: 'flex' },
                  gridTemplateRows: { xs: 'auto 1fr', sm: 'none' }
                }}
              >
                <Grid
                  size={{ xs: 4, sm: 4, md: 6 }}
                  order={{ sm: item.variant === 'text-left' ? 2 : 1 }}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: { xs: 1, sm: 'auto' },
                    overflow: 'hidden',
                    borderRadius: (theme) =>
                      isHappa ? theme.shape.borderRadius2 : theme.shape.borderRadius7,
                    ['&>img']: {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }
                  }}
                >
                  {item.image?.url ? (
                    <Image
                      src={item.image.url}
                      alt={item.image.alt || ''}
                      width={item.image.width || 800}
                      height={item.image.height || 450}
                      draggable={false}
                    />
                  ) : null}
                </Grid>

                <Grid
                  size={{ xs: 4, sm: 4, md: 6 }}
                  order={{ sm: item.variant === 'text-left' ? 1 : 2 }}
                  sx={(theme) => ({
                    position: 'relative',
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.text.primaryContrastText,
                    borderRadius: isHappa ? theme.shape.borderRadius2 : theme.shape.borderRadius7,
                    p: { xs: 3, sm: 6 }
                  })}
                >
                  <Box sx={{ display: 'grid', gap: 4 }}>
                    <Box sx={{ display: 'grid', gap: 4 }}>
                      <Typography variant="h6" component={is_first ? 'h1' : 'h2'}>
                        {item.title}
                      </Typography>
                      <Typography variant="textRegular" sx={{ minHeight: '2.1rem' }}>
                        {parse(item.text)}
                      </Typography>
                    </Box>
                    <Box>
                      {item.link?.url ? (
                        <Button
                          href={item.link.url}
                          variant="contained"
                          color={isHappa ? 'neutral' : 'secondary'}
                          icon="plus"
                        >
                          {item.link.title}
                        </Button>
                      ) : null}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            ))}
          </Box>
        </Box>

        {needsControls ? (
          <EmblaControls
            sx={{ mt: 4 }}
            navigation={false}
            justifyContent="center"
            scrollSnaps={scrollSnaps}
            selectedIndex={selectedIndex}
            prevBtnDisabled={prevBtnDisabled}
            nextBtnDisabled={nextBtnDisabled}
            onDotClick={(index) => emblaApi?.scrollTo(index)}
            scrollPrev={scrollPrev}
            scrollNext={scrollNext}
          />
        ) : null}
      </Container>
    </Box>
  )
}

export default BannerSlider
